if (!customElements.get("swatch-carousel")) {
  const SCROLL_POSITIONS = new Map();
  const SCROLL_TO_SELECTED = new Set();

  class SwatchCarousel extends HTMLElement {
    constructor() {
      super();
      this.boundUpdate = this.update.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onArrowClick = this.onArrowClick.bind(this);
      this.onChange = this.syncSelected.bind(this);
    }

    get scrollKey() {
      return this.dataset.drawerTarget || "";
    }

    onScroll() {
      if (this.scroller) {
        SCROLL_POSITIONS.set(this.scrollKey, this.scroller.scrollLeft);
      }
      this.update();
    }

    restoreScroll() {
      const saved = SCROLL_POSITIONS.get(this.scrollKey);
      if (!saved || !this.scroller) return;
      const prev = this.scroller.style.scrollBehavior;
      this.scroller.style.scrollBehavior = "auto";
      this.scroller.scrollLeft = saved;
      this.scroller.style.scrollBehavior = prev;
    }

    scrollSelectedIntoView() {
      const selected = this.options.find((o) => {
        const input = o.querySelector('input[type="radio"]');
        return input && input.checked;
      });
      if (!selected || !this.scroller) return;
      // Buffer clears the side arrows (~3.6rem) plus a little breathing room.
      const buffer = 48;
      const s = this.scroller.getBoundingClientRect();
      const i = selected.getBoundingClientRect();
      let delta = 0;
      if (i.left < s.left + buffer) {
        delta = i.left - s.left - buffer;
      } else if (i.right > s.right - buffer) {
        delta = i.right - s.right + buffer;
      }
      if (delta) this.scroller.scrollBy({ left: delta, behavior: "smooth" });
    }

    connectedCallback() {
      this.scroller = this.querySelector("[data-swatch-scroller]");
      this.prevArrow = this.querySelector('[data-swatch-arrow="prev"]');
      this.nextArrow = this.querySelector('[data-swatch-arrow="next"]');

      const field = this.closest(".variant-picker__color");
      this.viewAll = field && field.querySelector(".variant-picker__view-all");

      if (!this.scroller) return;

      this.options = Array.from(
        this.scroller.querySelectorAll(".variant-picker__option")
      );

      this.scroller.addEventListener("scroll", this.onScroll, { passive: true });
      this.prevArrow &&
        this.prevArrow.addEventListener("click", this.onArrowClick);
      this.nextArrow &&
        this.nextArrow.addEventListener("click", this.onArrowClick);
      this.addEventListener("change", this.onChange);

      if ("ResizeObserver" in window) {
        this.resizeObserver = new ResizeObserver(() => this.update());
        this.resizeObserver.observe(this.scroller);
      } else {
        window.addEventListener("resize", this.boundUpdate);
      }

      this.buildDrawer();

      // Initial measure now, then restore scroll (or reveal the drawer-chosen swatch) and re-measure after layout settles
      this.update();
      requestAnimationFrame(() => {
        if (SCROLL_TO_SELECTED.has(this.scrollKey)) {
          SCROLL_TO_SELECTED.delete(this.scrollKey);
          this.update();
          this.scrollSelectedIntoView();
        } else {
          this.restoreScroll();
        }
        this.update();
      });
      window.addEventListener("load", this.boundUpdate, { once: true });
      document.addEventListener("page:loaded", this.boundUpdate, { once: true });
    }

    disconnectedCallback() {
      this.scroller &&
        this.scroller.removeEventListener("scroll", this.onScroll);
      this.prevArrow &&
        this.prevArrow.removeEventListener("click", this.onArrowClick);
      this.nextArrow &&
        this.nextArrow.removeEventListener("click", this.onArrowClick);
      this.removeEventListener("change", this.onChange);
      this.resizeObserver && this.resizeObserver.disconnect();
      window.removeEventListener("resize", this.boundUpdate);
      window.removeEventListener("load", this.boundUpdate);
      document.removeEventListener("page:loaded", this.boundUpdate);
    }

    get isOverflowing() {
      return this.scroller.scrollWidth - this.scroller.clientWidth > 1;
    }

    update() {
      if (!this.scroller) return;
      const overflowing = this.isOverflowing;

      this.viewAll && this.viewAll.toggleAttribute("hidden", !overflowing);

      if (!overflowing) {
        this.prevArrow && this.prevArrow.setAttribute("hidden", "");
        this.nextArrow && this.nextArrow.setAttribute("hidden", "");
        return;
      }

      const { scrollLeft, scrollWidth, clientWidth } = this.scroller;
      const atStart = scrollLeft <= 1;
      const atEnd = scrollLeft + clientWidth >= scrollWidth - 1;

      this.prevArrow && this.prevArrow.toggleAttribute("hidden", atStart);
      this.nextArrow && this.nextArrow.toggleAttribute("hidden", atEnd);
    }

    onArrowClick(event) {
      const direction =
        event.currentTarget.dataset.swatchArrow === "prev" ? -1 : 1;
      const step = Math.round(this.scroller.clientWidth * 0.8);
      this.scroller.scrollBy({ left: direction * step, behavior: "smooth" });
    }

    buildDrawer() {
      // The picker fully re-renders on every selection, which orphans the previous drawer in <body>.
      // Keep exactly one drawer for this option: prefer the freshly rendered shell still inside this field-wrapper, otherwise the newest existing one.
      const bareId = (this.dataset.drawerTarget || "").replace(/^#/, "");
      const wrapper = this.closest(".variant-picker__field-wrapper");
      const all = Array.from(
        document.querySelectorAll("drawer-component")
      ).filter((d) => d.id === bareId);
      const inWrapper = wrapper && all.find((d) => wrapper.contains(d));
      const drawer = inWrapper || all[all.length - 1];
      all.forEach((d) => {
        if (d !== drawer) d.remove();
      });

      this.drawer = drawer;
      const list = drawer && drawer.querySelector("[data-swatch-list]");
      if (!list) return;
      list.innerHTML = "";

      const items = this.options.map((option) => {
        const input = option.querySelector('input[type="radio"]');
        const label = option.querySelector("label");
        if (!input || !label) return null;

        const name =
          option.dataset.tippyContent || input.value || label.textContent.trim();
        const background = label.style.getPropertyValue("--swatch-background");
        const focal = label.style.getPropertyValue("--swatch-focal-point");
        const soldout = option.classList.contains(
          "variant-picker__option--soldout"
        );

        const li = document.createElement("li");
        li.className = "variant-picker__swatch-item";
        if (soldout) li.classList.add("is-soldout");

        const button = document.createElement("button");
        button.type = "button";
        button.className = "variant-picker__swatch-item-btn";
        button.dataset.swatchTarget = input.id;

        const chip = document.createElement("span");
        chip.className =
          "variant-picker__option-button variant-picker__option-button--color variant-picker__swatch-item-chip";
        if (background)
          chip.style.setProperty("--swatch-background", background);
        if (focal) chip.style.setProperty("--swatch-focal-point", focal);

        const text = document.createElement("span");
        text.className = "variant-picker__swatch-item-name";
        text.textContent = name;

        button.append(chip, text);
        button.addEventListener("click", () => {
          SCROLL_TO_SELECTED.add(this.scrollKey);
          input.click();
          this.drawer && this.drawer.closeDrawer();
        });

        li.appendChild(button);
        li.swatchInput = input;
        return li;
      });

      items.forEach((li) => li && list.appendChild(li));
      this.drawerItems = items.filter(Boolean);
      this.syncSelected();
    }

    syncSelected() {
      if (!this.drawerItems) return;
      this.drawerItems.forEach((li) => {
        li.classList.toggle("is-selected", li.swatchInput.checked);
      });
    }
  }

  customElements.define("swatch-carousel", SwatchCarousel);
}
